<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import type { ArtworkFilters } from "~/components/molecules/ArtworkFeed.vue";
import { COLOR_BUCKETS, colorSwatchStyle } from "~/utils/colorBuckets";

const props = withDefaults(defineProps<{
  sortBy?: string;
  filters?: ArtworkFilters;
  /** Adds extra top spacing above the active-filter chips row (used where the controls row has no title beside it) */
  spacedChips?: boolean;
}>(), {
  spacedChips: false,
});

const emit = defineEmits<{
  "update:sortBy": [value: string | undefined];
  "update:filters": [value: ArtworkFilters];
}>();

// --- Filter input state ---
const priceMinInput = ref<string>("");
const priceMaxInput = ref<string>("");
const sizeMinInput = ref<string>("");
const sizeMaxInput = ref<string>("");
const orientationInput = ref<"portrait" | "landscape" | "square_circle" | "">("");
const mediumInput = ref<string[]>([]);
const colorsInput = ref<string[]>([]);
const excludeSoldInput = ref<boolean>(false);
const fastDispatchInput = ref<boolean>(false);
const onlySetsInput = ref<boolean>(false);

const MEDIUM_OPTIONS = [
  { label: "Paper", value: "Paper" },
  { label: "Canvas", value: "Canvas" },
  { label: "Sculpture", value: "Sculpture" },
  { label: "Textile", value: "Textile" },
  { label: "Mixed Media", value: "Mixed-Media" },
];

// --- Sync local state when filter is cleared externally ---
watch(() => props.filters, (f) => {
  if (!f?.priceMin && !f?.priceMax) {
    priceMinInput.value = "";
    priceMaxInput.value = "";
  } else {
    priceMinInput.value = f?.priceMin ? String(f.priceMin) : "";
    priceMaxInput.value = f?.priceMax ? String(f.priceMax) : "";
  }

  if (!f?.sizeMin && !f?.sizeMax) {
    sizeMinInput.value = "";
    sizeMaxInput.value = "";
  } else {
    sizeMinInput.value = f?.sizeMin ? String(f.sizeMin) : "";
    sizeMaxInput.value = f?.sizeMax ? String(f.sizeMax) : "";
  }

  orientationInput.value = f?.orientation ?? "";
  mediumInput.value = f?.medium ? [...f.medium] : [];
  colorsInput.value = f?.colors ? [...f.colors] : [];
  excludeSoldInput.value = f?.excludeSold ?? false;
  fastDispatchInput.value = f?.fastDispatch ?? false;
  onlySetsInput.value = f?.onlySets ?? false;
}, { immediate: true });

// --- Panel open state ---
const isPanelOpen = ref(false);
const panelRef = ref<HTMLElement | null>(null);
const filterButtonRef = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({});

const isSortOpen = ref(false);
const sortPanelRef = ref<HTMLElement | null>(null);
const sortButtonRef = ref<HTMLElement | null>(null);

// Positions the filter dropdown relative to the viewport so it always fits
// on screen and stays fully scrollable — a plain `absolute` dropdown can open
// far down a long page (e.g. the artist bio page) with most of its height
// below the visible viewport, leaving no way to scroll it into view. Measures
// the trigger *button* specifically (not the wrapping div, which also holds
// the dropdown itself) — measuring the wrapper would include the dropdown's
// own height once it renders, throwing the math off.
function positionFilterPanel() {
  const trigger = filterButtonRef.value;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const margin = 8;
  const preferredHeight = Math.min(window.innerHeight * 0.75, 600);
  const spaceBelow = window.innerHeight - rect.bottom - margin;
  const spaceAbove = rect.top - margin;

  let top: number;
  let maxHeight: number;
  if (spaceBelow >= 250 || spaceBelow >= spaceAbove) {
    top = rect.bottom + margin;
    maxHeight = Math.max(150, Math.min(preferredHeight, spaceBelow));
  } else {
    maxHeight = Math.max(150, Math.min(preferredHeight, spaceAbove));
    top = rect.top - margin - maxHeight;
  }

  panelStyle.value = {
    position: "fixed",
    top: `${top}px`,
    right: `${window.innerWidth - rect.right}px`,
    maxHeight: `${maxHeight}px`,
  };
}

// All swatches sit in one wrapping list (no sub-headings), in the order the client asked for. Only colors that match more than 1% of the live artworks are
// offered (an already-selected color always stays so it can be unticked). If the
// list can't be loaded, every color is shown.
const COLOR_ORDER = ["White", "Yellow", "Gold", "Orange", "Red", "Pink", "Purple", "Blue", "Green", "Brown", "Beige", "Grey", "Black", "Multicolor"];
const { data: visibleColorsData } = useFetch<{ colors: string[] }>("/api/artwork-feed/colors", {
  key: "artwork-visible-colors",
  server: false,
  lazy: true,
  default: () => null,
});
const colorSections = computed(() => {
  const buckets = COLOR_ORDER.map((name) => COLOR_BUCKETS.find((b) => b.name === name)).filter(
    (b): b is NonNullable<typeof b> =>
      !!b && (!visibleColorsData.value || visibleColorsData.value.colors.includes(b.name) || colorsInput.value.includes(b.name)),
  );
  return buckets.length > 0 ? [{ label: null as string | null, buckets }] : [];
});

// Bottom fade shown only while there is more panel content to scroll to, so
// the scrolling area doesn't look cut off.
const panelScrollRef = ref<HTMLElement | null>(null);
const canScrollMore = ref(false);

function updateScrollHint() {
  const el = panelScrollRef.value;
  if (!el) return;
  canScrollMore.value = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
}

watch(isPanelOpen, (open) => {
  if (open) nextTick(updateScrollHint);
});

function toggleFilterPanel() {
  // Computed against the button (always mounted) before the dropdown itself
  // mounts, so it opens with the correct position from its very first frame
  // instead of flashing unstyled for a tick.
  if (isPanelOpen.value) resetInputsToProps();
  else {
    positionFilterPanel();
    isSortOpen.value = false;
  }
  isPanelOpen.value = !isPanelOpen.value;
}

// Lets the page open the filter panel from elsewhere (the "Looking for something specific?" button)
function openFilterPanel() {
  if (!isPanelOpen.value) toggleFilterPanel();
}
defineExpose({ openFilterPanel });

// Opening Sort closes Filter (and discards its unsaved changes) — outside-click
// already covers the mouse, this covers opening it from the keyboard.
function toggleSort() {
  if (!isSortOpen.value && isPanelOpen.value) {
    resetInputsToProps();
    isPanelOpen.value = false;
  }
  isSortOpen.value = !isSortOpen.value;
}

// preventScroll: focusing a button must never jump the page.
function focusButton(el: HTMLElement | null) {
  el?.focus({ preventScroll: true });
}

// --- Sort handler ---
function onSortChange(value: string) {
  emit("update:sortBy", value || undefined);
  isSortOpen.value = false;
  focusButton(sortButtonRef.value);
}

// --- Unified filter apply ---
function applyFilters() {
  const pMinStr = String(priceMinInput.value);
  const pMaxStr = String(priceMaxInput.value);
  const sMinStr = String(sizeMinInput.value);
  const sMaxStr = String(sizeMaxInput.value);
  
  const pMinVal = Number(pMinStr);
  const pMaxVal = Number(pMaxStr);
  const sMinVal = Number(sMinStr);
  const sMaxVal = Number(sMaxStr);
  
  const hasPMin = pMinStr.trim() !== "" && !isNaN(pMinVal) && pMinVal >= 0;
  const hasPMax = pMaxStr.trim() !== "" && !isNaN(pMaxVal) && pMaxVal > 0;
  
  const hasSMin = sMinStr.trim() !== "" && !isNaN(sMinVal) && sMinVal >= 0;
  const hasSMax = sMaxStr.trim() !== "" && !isNaN(sMaxVal) && sMaxVal > 0;

  const updated: ArtworkFilters = { ...props.filters };

  // Price
  delete updated.priceMin;
  delete updated.priceMax;
  if (hasPMin) updated.priceMin = pMinVal;
  if (hasPMax) updated.priceMax = pMaxVal;

  // Size
  delete updated.sizeMin;
  delete updated.sizeMax;
  if (hasSMin) updated.sizeMin = sMinVal;
  if (hasSMax) updated.sizeMax = sMaxVal;

  // Orientation
  delete updated.orientation;
  if (orientationInput.value) updated.orientation = orientationInput.value;

  // Medium
  delete updated.medium;
  if (mediumInput.value.length > 0) updated.medium = [...mediumInput.value];

  // Colors
  delete updated.colors;
  if (colorsInput.value.length > 0) updated.colors = [...colorsInput.value];

  // Availability
  delete updated.excludeSold;
  if (excludeSoldInput.value) updated.excludeSold = true;

  // Fast Dispatch
  delete updated.fastDispatch;
  if (fastDispatchInput.value) updated.fastDispatch = true;

  // Only artwork sets
  delete updated.onlySets;
  if (onlySetsInput.value) updated.onlySets = true;

  emit("update:filters", updated);
  isPanelOpen.value = false;
  focusButton(filterButtonRef.value);
}

// --- Toggle orientation selection ---
function toggleOrientation(value: "portrait" | "landscape" | "square_circle") {
  orientationInput.value = orientationInput.value === value ? "" : value;
}

// --- Toggle medium selection ---
function toggleMedium(value: string) {
  const idx = mediumInput.value.indexOf(value);
  if (idx === -1) {
    mediumInput.value.push(value);
  } else {
    mediumInput.value.splice(idx, 1);
  }
}

// --- Toggle color selection ---
function toggleColor(value: string) {
  const idx = colorsInput.value.indexOf(value);
  if (idx === -1) {
    colorsInput.value.push(value);
  } else {
    colorsInput.value.splice(idx, 1);
  }
}

// --- Remove price filter ---
function removePriceFilter() {
  const updated = { ...props.filters };
  delete updated.priceMin;
  delete updated.priceMax;
  priceMinInput.value = "";
  priceMaxInput.value = "";
  emit("update:filters", updated);
}



// --- Remove size filter ---
function removeSizeFilter() {
  const updated = { ...props.filters };
  delete updated.sizeMin;
  delete updated.sizeMax;
  sizeMinInput.value = "";
  sizeMaxInput.value = "";
  emit("update:filters", updated);
}

// --- Remove orientation filter ---
function removeOrientationFilter() {
  const updated = { ...props.filters };
  delete updated.orientation;
  orientationInput.value = "";
  emit("update:filters", updated);
}

// --- Remove medium filter ---
function removeMediumFilter() {
  const updated = { ...props.filters };
  delete updated.medium;
  mediumInput.value = [];
  emit("update:filters", updated);
}

// --- Remove colors filter ---
function removeColorsFilter() {
  const updated = { ...props.filters };
  delete updated.colors;
  colorsInput.value = [];
  emit("update:filters", updated);
}

// --- Remove availability filter ---
function removeAvailabilityFilter() {
  const updated = { ...props.filters };
  delete updated.excludeSold;
  excludeSoldInput.value = false;
  emit("update:filters", updated);
}

// --- Remove fast dispatch filter ---
function removeFastDispatchFilter() {
  const updated = { ...props.filters };
  delete updated.fastDispatch;
  fastDispatchInput.value = false;
  emit("update:filters", updated);
}

// --- Remove only-sets filter ---
function removeOnlySetsFilter() {
  const updated = { ...props.filters };
  delete updated.onlySets;
  onlySetsInput.value = false;
  emit("update:filters", updated);
}

// --- Clear all ---
function clearAll() {
  priceMinInput.value = "";
  priceMaxInput.value = "";
  sizeMinInput.value = "";
  sizeMaxInput.value = "";
  orientationInput.value = "";
  mediumInput.value = [];
  colorsInput.value = [];
  excludeSoldInput.value = false;
  fastDispatchInput.value = false;
  onlySetsInput.value = false;
  emit("update:sortBy", undefined);
  emit("update:filters", {});
}

// --- Computed ---
const hasActiveFilter = computed(() => {
  return (
    props.filters?.priceMin !== undefined ||
    props.filters?.priceMax !== undefined ||
    props.filters?.sizeMin !== undefined ||
    props.filters?.sizeMax !== undefined ||
    props.filters?.orientation !== undefined ||
    (props.filters?.medium !== undefined && props.filters.medium.length > 0) ||
    (props.filters?.colors !== undefined && props.filters.colors.length > 0) ||
    props.filters?.excludeSold === true ||
    props.filters?.fastDispatch === true ||
    props.filters?.onlySets === true
  );
});

const activeFilterCount = computed(() => {
  let count = 0;
  if (props.filters?.priceMin !== undefined || props.filters?.priceMax !== undefined) count++;
  if (props.filters?.sizeMin !== undefined || props.filters?.sizeMax !== undefined) count++;
  if (props.filters?.orientation !== undefined) count++;
  if (props.filters?.medium !== undefined && props.filters.medium.length > 0) count++;
  if (props.filters?.colors !== undefined && props.filters.colors.length > 0) count++;
  if (props.filters?.excludeSold) count++;
  if (props.filters?.fastDispatch) count++;
  if (props.filters?.onlySets) count++;
  return count;
});

const activePriceLabel = computed(() => {
  const min = props.filters?.priceMin;
  const max = props.filters?.priceMax;
  const formatStr = (val: number) => Intl.NumberFormat("en-IN").format(val);

  if (min !== undefined && max !== undefined) {
    return `₹${formatStr(min)} – ₹${formatStr(max)}`;
  }
  if (min !== undefined) {
    return `From ₹${formatStr(min)}`;
  }
  if (max !== undefined) {
    return `Up to ₹${formatStr(max)}`;
  }
  return null;
});

const sortLabel = computed(() => {
  if (props.sortBy === "price_asc") return "Price: Low to High";
  if (props.sortBy === "price_desc") return "Price: High to Low";
  if (props.sortBy === "size_asc") return "Size: Small to Large";
  if (props.sortBy === "size_desc") return "Size: Large to Small";
  if (props.sortBy === "year_asc") return "Year: Oldest to Newest";
  if (props.sortBy === "year_desc") return "Year: Newest to Oldest";
  return null;
});

const activeSizeLabel = computed(() => {
  const min = props.filters?.sizeMin;
  const max = props.filters?.sizeMax;

  if (min !== undefined && max !== undefined) {
    return `${min}" – ${max}"`;
  }
  if (min !== undefined) {
    return `From ${min}"`;
  }
  if (max !== undefined) {
    return `Up to ${max}"`;
  }
  return null;
});

const orientationOptions: { value: "portrait" | "landscape" | "square_circle"; label: string }[] = [
  { value: "portrait", label: "Portrait" },
  { value: "landscape", label: "Landscape" },
  { value: "square_circle", label: "Square/Circle" },
];

const activeOrientationLabel = computed(() => {
  const value = props.filters?.orientation;
  return orientationOptions.find((o) => o.value === value)?.label ?? null;
});

const activeMediumLabel = computed(() => {
  const values = props.filters?.medium;
  if (!values || values.length === 0) return null;
  const labels = values.map((v) => MEDIUM_OPTIONS.find((o) => o.value === v)?.label ?? v);
  if (labels.length === 1) return labels[0];
  return `${labels[0]} +${labels.length - 1}`;
});
const activeColorsLabel = computed(() => {
  const values = props.filters?.colors;
  if (!values || values.length === 0) return null;
  if (values.length === 1) return values[0];
  return `${values[0]} +${values.length - 1}`;
});

// --- Reset local inputs to match the currently applied filters (discard unsaved changes) ---
function resetInputsToProps() {
  const f = props.filters;
  priceMinInput.value = f?.priceMin !== undefined ? String(f.priceMin) : "";
  priceMaxInput.value = f?.priceMax !== undefined ? String(f.priceMax) : "";
  sizeMinInput.value = f?.sizeMin !== undefined ? String(f.sizeMin) : "";
  sizeMaxInput.value = f?.sizeMax !== undefined ? String(f.sizeMax) : "";
  orientationInput.value = f?.orientation ?? "";
  mediumInput.value = f?.medium ? [...f.medium] : [];
  colorsInput.value = f?.colors ? [...f.colors] : [];
  excludeSoldInput.value = f?.excludeSold ?? false;
  fastDispatchInput.value = f?.fastDispatch ?? false;
  onlySetsInput.value = f?.onlySets ?? false;
}

// --- Close panel on outside click ---
function onOutsideClick(e: MouseEvent) {
  if (panelRef.value && !panelRef.value.contains(e.target as Node)) {
    if (isPanelOpen.value) resetInputsToProps();
    isPanelOpen.value = false;
  }
  if (sortPanelRef.value && !sortPanelRef.value.contains(e.target as Node)) {
    isSortOpen.value = false;
  }
}
// --- Close panel when the page scrolls ---
// The panel is `position: fixed` (placed once on open), so it would stay on
// screen while its trigger button scrolls away. Scrolling inside the panel
// itself doesn't reach `window`, so only a real page scroll closes it. A small
// threshold ignores tiny jumps (e.g. mobile keyboard focusing an input).
let scrollYAtOpen = 0;
watch(isPanelOpen, (open) => {
  if (open) scrollYAtOpen = window.scrollY;
});

function onPageScroll() {
  if (!isPanelOpen.value) return;
  if (Math.abs(window.scrollY - scrollYAtOpen) < 10) return;
  resetInputsToProps();
  isPanelOpen.value = false;
}

// Escape closes whichever popover is open and returns focus to its button.
// Does nothing (and doesn't swallow the key) when neither is open.
function onKeydown(e: KeyboardEvent) {
  if (e.key !== "Escape") return;
  if (isPanelOpen.value) {
    resetInputsToProps();
    isPanelOpen.value = false;
    focusButton(filterButtonRef.value);
  } else if (isSortOpen.value) {
    isSortOpen.value = false;
    focusButton(sortButtonRef.value);
  }
}

function onWindowResize() {
  if (isPanelOpen.value) positionFilterPanel();
}

onMounted(() => {
  document.addEventListener("mousedown", onOutsideClick);
  document.addEventListener("keydown", onKeydown);
  window.addEventListener("scroll", onPageScroll, { passive: true });
  window.addEventListener("resize", onWindowResize);
});
onUnmounted(() => {
  document.removeEventListener("mousedown", onOutsideClick);
  document.removeEventListener("keydown", onKeydown);
  window.removeEventListener("scroll", onPageScroll);
  window.removeEventListener("resize", onWindowResize);
});
</script>

<template>
  <div class="flex flex-col w-full">
    <!-- Controls Row -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-2 w-full">
      <!-- Left Side -->
      <div v-if="$slots['controls-left']" class="min-w-0">
        <slot name="controls-left"></slot>
      </div>

      <!-- Right Side -->
      <div class="flex flex-nowrap items-center justify-end gap-2 w-full md:w-auto mt-auto" :class="{ 'md:ml-auto': !$slots['controls-left'] }">
        <slot name="controls-prepend"></slot>

      <!-- Sort Dropdown -->
      <div class="relative shrink-0" ref="sortPanelRef">
        <button
          ref="sortButtonRef"
          type="button"
          @click="toggleSort"
          :class="[
            'inline-flex cursor-pointer items-center justify-center gap-2 rounded-[6px] border px-3.5 h-[38px] text-sm font-medium transition-colors',
            isSortOpen
              ? 'border-on-primary-heading bg-on-primary-heading text-white'
              : 'border-button-secondary-hover bg-button-secondary text-on-primary-heading hover:bg-button-secondary-hover',
          ]"
        >
          <span>{{ sortLabel || 'Sort: Default' }}</span>
          <Icon
            name="lucide:chevron-down"
            class="size-3 transition-transform duration-200"
            :class="{ 'rotate-180': isSortOpen }"
          />
        </button>

        <!-- Sort Dropdown Panel -->
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-1"
        >
          <div
            v-if="isSortOpen"
            class="absolute right-0 top-full z-50 mt-2 w-48 rounded-[6px] border border-on-primary-border bg-white p-2 shadow-lg flex flex-col gap-1"
          >
            <button
              type="button"
              @click="onSortChange('')"
              :class="[
                'flex w-full items-center justify-between rounded-[4px] px-3 py-2 text-sm font-medium transition-colors hover:bg-button-secondary cursor-pointer',
                !sortBy ? 'bg-button-secondary text-on-primary-heading' : 'text-on-primary-body hover:text-on-primary-heading'
              ]"
            >
              <span class="truncate text-left">Default</span>
              <Icon v-if="!sortBy" name="lucide:check" class="size-3.5 shrink-0 ml-2" />
            </button>
            <button
              type="button"
              @click="onSortChange('price_asc')"
              :class="[
                'flex w-full items-center justify-between rounded-[4px] px-3 py-2 text-sm font-medium transition-colors hover:bg-button-secondary cursor-pointer',
                sortBy === 'price_asc' ? 'bg-button-secondary text-on-primary-heading' : 'text-on-primary-body hover:text-on-primary-heading'
              ]"
            >
              <span class="truncate text-left">Price: Low to High</span>
              <Icon v-if="sortBy === 'price_asc'" name="lucide:check" class="size-3.5 shrink-0 ml-2" />
            </button>
            <button
              type="button"
              @click="onSortChange('price_desc')"
              :class="[
                'flex w-full items-center justify-between rounded-[4px] px-3 py-2 text-sm font-medium transition-colors hover:bg-button-secondary cursor-pointer',
                sortBy === 'price_desc' ? 'bg-button-secondary text-on-primary-heading' : 'text-on-primary-body hover:text-on-primary-heading'
              ]"
            >
              <span class="truncate text-left">Price: High to Low</span>
              <Icon v-if="sortBy === 'price_desc'" name="lucide:check" class="size-3.5 shrink-0 ml-2" />
            </button>
            <button
              type="button"
              @click="onSortChange('size_asc')"
              :class="[
                'flex w-full items-center justify-between rounded-[4px] px-3 py-2 text-sm font-medium transition-colors hover:bg-button-secondary cursor-pointer',
                sortBy === 'size_asc' ? 'bg-button-secondary text-on-primary-heading' : 'text-on-primary-body hover:text-on-primary-heading'
              ]"
            >
              <span class="truncate text-left">Size: Small to Large</span>
              <Icon v-if="sortBy === 'size_asc'" name="lucide:check" class="size-3.5 shrink-0 ml-2" />
            </button>
            <button
              type="button"
              @click="onSortChange('size_desc')"
              :class="[
                'flex w-full items-center justify-between rounded-[4px] px-3 py-2 text-sm font-medium transition-colors hover:bg-button-secondary cursor-pointer',
                sortBy === 'size_desc' ? 'bg-button-secondary text-on-primary-heading' : 'text-on-primary-body hover:text-on-primary-heading'
              ]"
            >
              <span class="truncate text-left">Size: Large to Small</span>
              <Icon v-if="sortBy === 'size_desc'" name="lucide:check" class="size-3.5 shrink-0 ml-2" />
            </button>
            <button
              type="button"
              @click="onSortChange('year_desc')"
              :class="[
                'flex w-full items-center justify-between rounded-[4px] px-3 py-2 text-sm font-medium transition-colors hover:bg-button-secondary cursor-pointer',
                sortBy === 'year_desc' ? 'bg-button-secondary text-on-primary-heading' : 'text-on-primary-body hover:text-on-primary-heading'
              ]"
            >
              <span class="truncate text-left">Year: Newest to Oldest</span>
              <Icon v-if="sortBy === 'year_desc'" name="lucide:check" class="size-3.5 shrink-0 ml-2" />
            </button>
            <button
              type="button"
              @click="onSortChange('year_asc')"
              :class="[
                'flex w-full items-center justify-between rounded-[4px] px-3 py-2 text-sm font-medium transition-colors hover:bg-button-secondary cursor-pointer',
                sortBy === 'year_asc' ? 'bg-button-secondary text-on-primary-heading' : 'text-on-primary-body hover:text-on-primary-heading'
              ]"
            >
              <span class="truncate text-left">Year: Oldest to Newest</span>
              <Icon v-if="sortBy === 'year_asc'" name="lucide:check" class="size-3.5 shrink-0 ml-2" />
            </button>
          </div>
        </Transition>
      </div>

      <!-- Price Filter Button -->
      <div class="relative shrink-0" ref="panelRef">
        <button
          ref="filterButtonRef"
          type="button"
          @click="toggleFilterPanel"
          :class="[
            'inline-flex cursor-pointer items-center justify-center size-[38px] rounded-full border transition-colors',
            isPanelOpen
              ? 'border-on-primary-heading bg-on-primary-heading text-white'
              : 'border-button-secondary-hover bg-button-secondary text-on-primary-heading hover:bg-button-secondary-hover',
          ]"
          aria-label="Filter"
        >
          <Icon name="lucide:sliders-horizontal" class="size-4" />
          <span
            v-if="activeFilterCount > 0"
            class="absolute -top-1 -right-1 flex size-3.5 items-center justify-center rounded-full shadow-sm text-[9px] font-bold"
            :class="isPanelOpen ? 'bg-white text-on-primary-heading' : 'bg-on-primary-heading text-white'"
          >
            {{ activeFilterCount }}
          </span>
        </button>

        <!-- Unified Filter Dropdown -->
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-1"
        >
          <div
            v-if="isPanelOpen"
            :style="panelStyle"
            class="z-50 w-84 flex flex-col overflow-hidden rounded-[6px] border border-on-primary-border bg-white shadow-lg"
          >
           <!-- Scrollable body (footer with Apply stays outside so it's always visible) -->
           <div class="relative flex min-h-0 flex-1 flex-col">
            <div
              ref="panelScrollRef"
              class="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4"
              @scroll.passive="updateScrollHint"
            >
            <!-- Price Section -->
            <div class="mb-2 text-sm font-medium text-on-primary-heading">Price</div>
            <div class="flex gap-2 items-center mb-4">
              <div class="relative flex-1">
                <span class="absolute inset-y-0 left-3 flex items-center text-sm text-on-primary-body">₹</span>
                <input
                  v-model="priceMinInput"
                  type="number"
                  min="0"
                  placeholder="Min"
                  class="w-full rounded-[6px] border border-on-primary-border py-2 pl-6 pr-2 text-sm text-on-primary-heading placeholder:text-on-primary-body/50 focus:border-on-primary-heading focus:outline-none"
                  @keydown.enter="applyFilters"
                />
              </div>
              <span class="text-on-primary-body text-sm font-medium">to</span>
              <div class="relative flex-1">
                <span class="absolute inset-y-0 left-3 flex items-center text-sm text-on-primary-body">₹</span>
                <input
                  v-model="priceMaxInput"
                  type="number"
                  min="0"
                  placeholder="Max"
                  class="w-full rounded-[6px] border border-on-primary-border py-2 pl-6 pr-2 text-sm text-on-primary-heading placeholder:text-on-primary-body/50 focus:border-on-primary-heading focus:outline-none"
                  @keydown.enter="applyFilters"
                />
              </div>
            </div>

            <hr class="border-on-primary-border my-4" />

            <!-- Size Section -->
            <div class="mb-2 text-sm font-medium text-on-primary-heading">Size (in inches)</div>
            <div class="flex gap-2 items-center mb-4">
              <div class="relative flex-1">
                <input
                  v-model="sizeMinInput"
                  type="number"
                  min="0"
                  placeholder="Min"
                  class="w-full rounded-[6px] border border-on-primary-border py-2 px-3 text-sm text-on-primary-heading placeholder:text-on-primary-body/50 focus:border-on-primary-heading focus:outline-none"
                  @keydown.enter="applyFilters"
                />
              </div>
              <span class="text-on-primary-body text-sm font-medium">to</span>
              <div class="relative flex-1">
                <input
                  v-model="sizeMaxInput"
                  type="number"
                  min="0"
                  placeholder="Max"
                  class="w-full rounded-[6px] border border-on-primary-border py-2 px-3 text-sm text-on-primary-heading placeholder:text-on-primary-body/50 focus:border-on-primary-heading focus:outline-none"
                  @keydown.enter="applyFilters"
                />
              </div>
            </div>

            <hr class="border-on-primary-border my-4" />

            <!-- Orientation Section -->
            <div class="mb-2 text-sm font-medium text-on-primary-heading">Orientation</div>
            <div class="grid grid-cols-2 gap-2 mb-4">
              <button
                v-for="(option, index) in orientationOptions"
                :key="option.value"
                type="button"
                @click="toggleOrientation(option.value)"
                :class="[
                  'rounded-[6px] border py-2 px-2 text-sm font-medium transition-colors cursor-pointer flex items-center justify-center gap-2',
                  index === 2 ? 'col-span-2' : '',
                  orientationInput === option.value
                    ? 'border-on-primary-heading bg-on-primary-heading text-white'
                    : 'border-on-primary-border text-on-primary-body hover:bg-button-secondary',
                ]"
              >
                <span>{{ option.label }}</span>
                <!-- Portrait Icon (Vertical Frame) -->
                <svg v-if="option.value === 'portrait'" class="w-3 h-4 shrink-0" viewBox="0 0 12 16" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="1" y="1" width="10" height="14" rx="1.5" />
                </svg>
                <!-- Landscape Icon (Horizontal Frame) -->
                <svg v-else-if="option.value === 'landscape'" class="w-4 h-3 shrink-0" viewBox="0 0 16 12" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="1" y="1" width="14" height="10" rx="1.5" />
                </svg>
                <!-- Square/Circle Icon (Square / Circle Frames) -->
                <svg v-else-if="option.value === 'square_circle'" class="w-[34px] h-3.5 shrink-0" viewBox="0 0 34 14" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="1" y="1" width="12" height="12" rx="1.5" />
                  <line x1="16" y1="12" x2="19" y2="2" stroke-linecap="round" />
                  <circle cx="27" cy="7" r="6" />
                </svg>
              </button>
            </div>

            <hr class="border-on-primary-border my-4" />

            <!-- Medium Section -->
            <div class="mb-2 text-sm font-medium text-on-primary-heading">Category</div>
            <div class="flex flex-wrap gap-2 mb-4">
              <button
                v-for="option in MEDIUM_OPTIONS"
                :key="option.value"
                type="button"
                @click="toggleMedium(option.value)"
                :class="[
                  'rounded-[6px] border py-1.5 px-3 text-sm font-medium transition-colors cursor-pointer',
                  mediumInput.includes(option.value)
                    ? 'border-on-primary-heading bg-on-primary-heading text-white'
                    : 'border-on-primary-border text-on-primary-body hover:bg-button-secondary',
                ]"
              >
                {{ option.label }}
              </button>
            </div>

            <hr class="border-on-primary-border my-4" />

            <!-- Color Section -->
            <div id="color-filter-heading" class="mb-2 text-sm font-medium text-on-primary-heading">Colors</div>
            <div class="mb-4 flex flex-col gap-3" role="group" aria-labelledby="color-filter-heading">
              <div
                v-for="section in colorSections"
                :key="section.label ?? 'flat'"
                role="group"
                :aria-label="section.label ?? 'Colors'"
              >
                <div v-if="section.label" class="mb-1.5 text-xs font-medium text-on-primary-heading">{{ section.label }}</div>
                <div class="flex flex-wrap items-center gap-2">
                  <button
                    v-for="bucket in section.buckets"
                    :key="bucket.name"
                    type="button"
                    :aria-pressed="colorsInput.includes(bucket.name)"
                    @click="toggleColor(bucket.name)"
                    :class="[
                      'inline-flex items-center gap-1.5 rounded-[6px] border py-1.5 px-3 text-sm font-medium transition-colors',
                      colorsInput.includes(bucket.name)
                        ? 'border-on-primary-heading bg-on-primary-heading text-white cursor-pointer'
                        : 'border-on-primary-border text-on-primary-body hover:bg-button-secondary cursor-pointer',
                    ]"
                  >
                    <span
                      class="size-3 rounded-full border border-black/25 shrink-0"
                      :style="colorSwatchStyle(bucket.name)"
                    ></span>
                    {{ bucket.name }}
                    <Icon v-if="colorsInput.includes(bucket.name)" name="lucide:check" class="size-3.5 shrink-0" />
                  </button>
                </div>
              </div>
            </div>
            <hr class="border-on-primary-border my-4" />

            <!-- Availability Section -->
            <div class="mb-4">
              <label class="flex items-center gap-2 cursor-pointer group mb-2">
                <div class="relative flex items-center justify-center size-5 rounded border transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-on-primary-heading" :class="fastDispatchInput ? 'border-on-primary-heading bg-on-primary-heading text-white' : 'border-on-primary-border bg-white text-transparent group-hover:border-on-primary-heading'">
                  <Icon name="lucide:check" class="size-3.5" />
                  <input type="checkbox" v-model="fastDispatchInput" class="absolute opacity-0 cursor-pointer w-full h-full" />
                </div>
                <span class="text-sm font-medium text-on-primary-heading">Dispatches within one week</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer group mb-2">
                <div class="relative flex items-center justify-center size-5 rounded border transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-on-primary-heading" :class="excludeSoldInput ? 'border-on-primary-heading bg-on-primary-heading text-white' : 'border-on-primary-border bg-white text-transparent group-hover:border-on-primary-heading'">
                  <Icon name="lucide:check" class="size-3.5" />
                  <input type="checkbox" v-model="excludeSoldInput" class="absolute opacity-0 cursor-pointer w-full h-full" />
                </div>
                <span class="text-sm font-medium text-on-primary-heading">Exclude sold and reserved artworks</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer group">
                <div class="relative flex items-center justify-center size-5 rounded border transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-on-primary-heading" :class="onlySetsInput ? 'border-on-primary-heading bg-on-primary-heading text-white' : 'border-on-primary-border bg-white text-transparent group-hover:border-on-primary-heading'">
                  <Icon name="lucide:check" class="size-3.5" />
                  <input type="checkbox" v-model="onlySetsInput" class="absolute opacity-0 cursor-pointer w-full h-full" />
                </div>
                <span class="text-sm font-medium text-on-primary-heading">Show me only artwork sets</span>
              </label>
            </div>

            </div>
            <!-- Bottom fade: hints that the panel scrolls -->
            <div
              class="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-white to-transparent transition-opacity duration-150"
              :class="canScrollMore ? 'opacity-100' : 'opacity-0'"
            ></div>
           </div>

           <!-- Sticky footer -->
           <div class="shrink-0 border-t border-on-primary-border bg-white p-4">
            <button
              type="button"
              @click="applyFilters"
              class="w-full rounded-[6px] border border-button-primary-hover bg-button-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-button-primary-hover cursor-pointer"
            >
              Apply Filters
            </button>
           </div>
          </div>
        </Transition>
      </div>
    </div>
    <!-- Close Controls Row -->
    </div>

    <!-- Active Chips Row -->
    <div v-if="hasActiveFilter" class="flex flex-wrap items-center justify-end gap-2 mt-2 bg-[var(--color-primary,#fff)]" :class="spacedChips ? 'pt-1' : 'pt-0'">
      <!-- Price chip -->
      <span
        v-if="activePriceLabel"
        class="inline-flex items-center gap-1.5 rounded-[6px] border border-on-primary-border bg-button-secondary px-3 py-1.5 text-xs font-medium text-on-primary-heading"
      >
        {{ activePriceLabel }}
        <button
          type="button"
          @click="removePriceFilter"
          class="flex items-center text-on-primary-body hover:text-on-primary-heading cursor-pointer transition-colors"
          aria-label="Remove price filter"
        >
          <Icon name="lucide:x" class="size-3" />
        </button>
      </span>

      <!-- Size chip -->
      <span
        v-if="activeSizeLabel"
        class="inline-flex items-center gap-1.5 rounded-[6px] border border-on-primary-border bg-button-secondary px-3 py-1.5 text-xs font-medium text-on-primary-heading"
      >
        {{ activeSizeLabel }}
        <button
          type="button"
          @click="removeSizeFilter"
          class="flex items-center text-on-primary-body hover:text-on-primary-heading cursor-pointer transition-colors"
          aria-label="Remove size filter"
        >
          <Icon name="lucide:x" class="size-3" />
        </button>
      </span>

      <!-- Orientation chip -->
      <span
        v-if="activeOrientationLabel"
        class="inline-flex items-center gap-1.5 rounded-[6px] border border-on-primary-border bg-button-secondary px-3 py-1.5 text-xs font-medium text-on-primary-heading"
      >
        {{ activeOrientationLabel }}
        <button
          type="button"
          @click="removeOrientationFilter"
          class="flex items-center text-on-primary-body hover:text-on-primary-heading cursor-pointer transition-colors"
          aria-label="Remove orientation filter"
        >
          <Icon name="lucide:x" class="size-3" />
        </button>
      </span>

      <!-- Medium chip -->
      <span
        v-if="activeMediumLabel"
        class="inline-flex items-center gap-1.5 rounded-[6px] border border-on-primary-border bg-button-secondary px-3 py-1.5 text-xs font-medium text-on-primary-heading"
      >
        {{ activeMediumLabel }}
        <button
          type="button"
          @click="removeMediumFilter"
          class="flex items-center text-on-primary-body hover:text-on-primary-heading cursor-pointer transition-colors"
          aria-label="Remove medium filter"
        >
          <Icon name="lucide:x" class="size-3" />
        </button>
      </span>

      <!-- Colors chip -->
      <span
        v-if="activeColorsLabel"
        class="inline-flex items-center gap-1.5 rounded-[6px] border border-on-primary-border bg-button-secondary px-3 py-1.5 text-xs font-medium text-on-primary-heading"
      >
        <span class="flex items-center -space-x-1">
          <span
            v-for="c in (props.filters?.colors ?? []).slice(0, 3)"
            :key="c"
            class="size-3 rounded-full border border-black/25 ring-1 ring-white"
            :style="colorSwatchStyle(c)"
          ></span>
        </span>
        {{ activeColorsLabel }}
        <button
          type="button"
          @click="removeColorsFilter"
          class="flex items-center text-on-primary-body hover:text-on-primary-heading cursor-pointer transition-colors"
          aria-label="Remove color filter"
        >
          <Icon name="lucide:x" class="size-3" />
        </button>
      </span>

      <!-- Availability chip -->
      <span
        v-if="props.filters?.excludeSold"
        class="inline-flex items-center gap-1.5 rounded-[6px] border border-on-primary-border bg-button-secondary px-3 py-1.5 text-xs font-medium text-on-primary-heading"
      >
        Excluding Sold/Reserved
        <button
          type="button"
          @click="removeAvailabilityFilter"
          class="flex items-center text-on-primary-body hover:text-on-primary-heading cursor-pointer transition-colors"
          aria-label="Remove availability filter"
        >
          <Icon name="lucide:x" class="size-3" />
        </button>
      </span>

      <!-- Fast Dispatch chip -->
      <span
        v-if="props.filters?.fastDispatch"
        class="inline-flex items-center gap-1.5 rounded-[6px] border border-on-primary-border bg-button-secondary px-3 py-1.5 text-xs font-medium text-on-primary-heading"
      >
        Dispatches within 1 week
        <button
          type="button"
          @click="removeFastDispatchFilter"
          class="flex items-center text-on-primary-body hover:text-on-primary-heading cursor-pointer transition-colors"
          aria-label="Remove fast dispatch filter"
        >
          <Icon name="lucide:x" class="size-3" />
        </button>
      </span>

      <!-- Only Sets chip -->
      <span
        v-if="props.filters?.onlySets"
        class="inline-flex items-center gap-1.5 rounded-[6px] border border-on-primary-border bg-button-secondary px-3 py-1.5 text-xs font-medium text-on-primary-heading"
      >
        Artwork sets only
        <button
          type="button"
          @click="removeOnlySetsFilter"
          class="flex items-center text-on-primary-body hover:text-on-primary-heading cursor-pointer transition-colors"
          aria-label="Remove only-sets filter"
        >
          <Icon name="lucide:x" class="size-3" />
        </button>
      </span>

      <!-- Clear All -->
      <button
        type="button"
        @click="clearAll"
        class="text-xs font-medium text-on-primary-body underline underline-offset-2 transition-colors hover:text-on-primary-heading cursor-pointer"
      >
        Clear all
      </button>
    </div>
  </div>
</template>
